import { create } from 'zustand';
import { driverShiftService } from '../services/driverShifts';
import type { ApiError, DriverActiveShift, DriverUpcomingAssignment, EndDriverShiftPayload } from '../types';

interface DriverShiftState {
    currentShift: DriverActiveShift | null;
    upcomingAssignment: DriverUpcomingAssignment | null;
    isLoading: boolean;
    isSubmitting: boolean;
    error: ApiError | null;
    loadCurrentShift: () => Promise<void>;
    startCurrentShift: () => Promise<boolean>;
    pauseMatching: () => Promise<boolean>;
    resumeMatching: () => Promise<boolean>;
    endShift: (payload?: EndDriverShiftPayload) => Promise<boolean>;
    clearShift: () => void;
}

function normalizeError(error: unknown, fallbackMessage: string): ApiError {
    const apiError = error as ApiError;

    return apiError?.message ? apiError : { message: fallbackMessage };
}

export const useDriverShiftStore = create<DriverShiftState>((set) => ({
    currentShift: null,
    upcomingAssignment: null,
    isLoading: false,
    isSubmitting: false,
    error: null,

    loadCurrentShift: async (): Promise<void> => {
        set({ isLoading: true, error: null });

        try {
            const response = await driverShiftService.getCurrentShift();
            set({
                currentShift: response.data,
                upcomingAssignment: response.upcoming_assignment ?? null,
                isLoading: false,
                error: null,
            });
        } catch (error) {
            set({
                isLoading: false,
                error: normalizeError(error, 'No se pudo cargar la jornada activa.'),
            });
        }
    },

    startCurrentShift: async (): Promise<boolean> => {
        set({ isSubmitting: true, error: null });

        try {
            const response = await driverShiftService.startCurrentShift();
            set({
                currentShift: response.data,
                upcomingAssignment: null,
                isSubmitting: false,
                error: null,
            });
            return true;
        } catch (error) {
            set({
                isSubmitting: false,
                error: normalizeError(error, 'No se pudo iniciar la jornada.'),
            });
            return false;
        }
    },

    pauseMatching: async (): Promise<boolean> => {
        set({ isSubmitting: true, error: null });

        try {
            const response = await driverShiftService.pauseMatching();
            set({ currentShift: response.data, isSubmitting: false, error: null });
            return true;
        } catch (error) {
            set({
                isSubmitting: false,
                error: normalizeError(error, 'No se pudo pausar el matching.'),
            });
            return false;
        }
    },

    resumeMatching: async (): Promise<boolean> => {
        set({ isSubmitting: true, error: null });

        try {
            const response = await driverShiftService.resumeMatching();
            set({ currentShift: response.data, isSubmitting: false, error: null });
            return true;
        } catch (error) {
            set({
                isSubmitting: false,
                error: normalizeError(error, 'No se pudo reanudar el matching.'),
            });
            return false;
        }
    },

    endShift: async (payload: EndDriverShiftPayload = { ended_reason: 'end_of_day' }): Promise<boolean> => {
        set({ isSubmitting: true, error: null });

        try {
            await driverShiftService.endShift(payload);
            const currentResponse = await driverShiftService.getCurrentShift();
            set({
                currentShift: currentResponse.data,
                upcomingAssignment: currentResponse.upcoming_assignment ?? null,
                isSubmitting: false,
                error: null,
            });
            return true;
        } catch (error) {
            set({
                isSubmitting: false,
                error: normalizeError(error, 'No se pudo terminar la jornada.'),
            });
            return false;
        }
    },

    clearShift: (): void => {
        set({
            currentShift: null,
            upcomingAssignment: null,
            isLoading: false,
            isSubmitting: false,
            error: null,
        });
    },
}));
